import React, { useEffect, useRef } from "react";

/**
 * Fond de hero CINÉMATIQUE synchronisé au scroll, sur TOUS les appareils.
 *
 * Séquence d'images (façon Apple) : on dessine sur un <canvas> l'image
 * correspondant à la progression du scroll dans [data-hero-scrub]. Dessiner une
 * image déjà décodée est instantané → aucun "seek" vidéo → fluide.
 *
 * FLUIDITÉ — deux mécanismes :
 *  1) Inertie : une boucle rAF fait converger en douceur la position affichée
 *     vers la position-cible dérivée du scroll.
 *  2) FONDU ENTRE IMAGES (cross-fade) : au lieu de "sauter" d'une image à la
 *     suivante, on dessine l'image i0 PUIS l'image i0+1 par-dessus avec une
 *     opacité = partie décimale de la position → transition continue, comme si
 *     on avait beaucoup plus d'images.
 *
 * Rendu COVER (plein écran) : le jeu mobile est en PORTRAIT (9:16) et le desktop
 * en PAYSAGE (16:9), donc cover remplit l'écran dans les deux cas.
 */
export default function HeroScrollFrames({
  count = 84,
  mobileCount = 40,
  dir = "/hero-frames",
  mobileDir = "/hero-frames-m",
  pad = 3,
  className = "",
}) {
  const canvasRef = useRef(null);
  const targetRef = useRef(0);
  const dispRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const scrubEl = canvas.closest("[data-hero-scrub]") || canvas.parentElement;
    const box = canvas.parentElement;
    const mobile =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(max-width: 767px)").matches;
    const useDir = mobile ? mobileDir : dir;
    const N = mobile ? mobileCount : count;
    const imgs = new Array(N);
    const srcOf = (i) => `${useDir}/frame-${String(i + 1).padStart(pad, "0")}.jpg`;

    const drawCover = (img, alpha) => {
      if (!img || !img.complete || !img.naturalWidth) return false;
      const cw = canvas.width, ch = canvas.height;
      const ir = img.naturalWidth / img.naturalHeight, cr = cw / ch;
      let dw, dh, dx, dy;
      if (ir > cr) { dh = ch; dw = ch * ir; dx = (cw - dw) / 2; dy = 0; }
      else { dw = cw; dh = cw / ir; dx = 0; dy = (ch - dh) / 2; }
      ctx.globalAlpha = alpha;
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.globalAlpha = 1;
      return true;
    };

    // Rend la position flottante "pos" avec fondu entre l'image i0 et i0+1.
    const render = (pos) => {
      const i0 = Math.max(0, Math.min(N - 1, Math.floor(pos)));
      const i1 = Math.min(N - 1, i0 + 1);
      const f = pos - Math.floor(pos);
      if (!drawCover(imgs[i0], 1)) return false; // base pas encore chargée
      if (i1 !== i0 && f > 0.012) drawCover(imgs[i1], f);
      return true;
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 2.5 : 1.75);
      const w = box.clientWidth, h = box.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      render(dispRef.current);
    };

    let firstLoaded = false;
    for (let i = 0; i < N; i++) {
      const im = new Image();
      im.decoding = "async";
      im.src = srcOf(i);
      if (i === 0) {
        im.onload = () => {
          if (firstLoaded) return;
          firstLoaded = true;
          resize();
          render(dispRef.current);
        };
      }
      imgs[i] = im;
    }

    const computeTarget = () => {
      const rect = scrubEl.getBoundingClientRect();
      const range = Math.max(scrubEl.offsetHeight - window.innerHeight, 1);
      const scrolled = Math.min(Math.max(-rect.top, 0), range);
      const progress = scrolled / range;
      targetRef.current = progress * (N - 1);
    };

    let lastRendered = -1;
    const tick = () => {
      const t = targetRef.current;
      const d = dispRef.current;
      const diff = t - d;
      dispRef.current = Math.abs(diff) < 0.02 ? t : d + diff * 0.2;
      if (Math.abs(dispRef.current - lastRendered) > 0.008) {
        if (render(dispRef.current)) lastRendered = dispRef.current;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    computeTarget();
    dispRef.current = targetRef.current;
    const onScroll = () => computeTarget();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", resize);
    resize();
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [count, mobileCount, dir, mobileDir, pad]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
